import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  InputAdornment,
  Link,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { patientService, Patient, PatientCreate } from '../services/patientService';

const PatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);

  // UI State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { register, handleSubmit, reset, clearErrors, formState: { errors } } = useForm<PatientCreate & { status?: string }>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  // Filter patients based on search query
  const filteredPatients = patients.filter(patient => {
    const query = searchQuery.toLowerCase();
    return (
      patient.full_name.toLowerCase().includes(query) ||
      patient.email?.toLowerCase().includes(query) ||
      patient.phone?.toLowerCase().includes(query)
    );
  });

  // Load patients data
  useEffect(() => {
    const loadPatients = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load patients
        const patientsData = await patientService.getPatients(currentTenant.id);
        setPatients(patientsData);
      } catch (err) {
        console.error('Error loading patients:', err);
        setError(err instanceof Error ? err.message : 'Failed to load patients');
      } finally {
        setLoading(false);
      }
    };

    loadPatients();
  }, [user, currentTenant, refreshKey]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, patient: Patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, patient: Patient) => {
    setStatusAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleViewDetails = () => {
    if (selectedPatient) {
      navigate(`/patients/${selectedPatient.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedPatient) {
      setEditingPatient(selectedPatient);
      reset({
        first_name: selectedPatient.first_name,
        last_name: selectedPatient.last_name,
        email: selectedPatient.email || '',
        phone: selectedPatient.phone || '',
        date_of_birth: selectedPatient.date_of_birth,
        status: selectedPatient.status,
      });
      setAddDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleScheduleMeeting = () => {
    if (selectedPatient) {
      // Navigate to meetings page with patient pre-selected
      navigate(`/meetings?patient_id=${selectedPatient.id}&action=schedule`);
    }
    handleMenuClose();
  };

  const handleFinish = async () => {
    if (!selectedPatient) return;

    try {
      await patientService.finishPatient(selectedPatient.id);
      toast.success('Patient treatment finished successfully');

      // Update local state
      setPatients(patients.map(p =>
        p.id === selectedPatient.id
          ? { ...p, status: 'finished' as const }
          : p
      ));
    } catch (error) {
      console.error('Error finishing patient:', error);
      toast.error('Failed to finish patient treatment');
    }
    handleStatusMenuClose();
  };

  const handleRevive = async () => {
    if (!selectedPatient) return;

    try {
      await patientService.revivePatient(selectedPatient.id);
      toast.success('Patient revived successfully');

      // Update local state
      setPatients(patients.map(p =>
        p.id === selectedPatient.id
          ? { ...p, status: 'active' as const }
          : p
      ));
    } catch (error) {
      console.error('Error reviving patient:', error);
      toast.error('Failed to revive patient');
    }
    handleStatusMenuClose();
  };

  const handleAddPatient = () => {
    setEditingPatient(null);
    reset({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
    });
    clearErrors();
    setAddDialogOpen(true);
  };

  const onSubmit = async (data: PatientCreate) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      // Clean up empty strings to null for optional fields
      const patientData = {
        ...data,
        tenant_id: currentTenant.id,
        date_of_birth: data.date_of_birth || null,
        email: data.email || null,
      };

      if (editingPatient) {
        // Update existing patient
        const updatedPatient = await patientService.updatePatient(editingPatient.id, patientData);
        setPatients(patients.map(p => p.id === editingPatient.id ? updatedPatient : p));
        toast.success('Patient updated successfully');
      } else {
        // Create new patient
        const newPatient = await patientService.createPatient(patientData);
        setPatients([...patients, newPatient]);
        toast.success('Patient created successfully');
      }

      setAddDialogOpen(false);
      setEditingPatient(null);
      reset();
    } catch (error) {
      console.error('Error saving patient:', error);
      toast.error('Failed to save patient');
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'default';
  };

  const calculateAge = (dateOfBirth: string | null | undefined) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatLastMeeting = (lastMeetingDate: string | null) => {
    if (!lastMeetingDate) {
      return 'Never';
    }
    const date = new Date(lastMeetingDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading patients: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Patients ({filteredPatients.length}{patients.length !== filteredPatients.length ? ` of ${patients.length}` : ''})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPatient}
          disabled={!currentTenant}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            padding: { xs: '6px 16px', sm: '8px 22px' }
          }}
        >
          Add Patient
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search patients by name, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!currentTenant && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {user?.role === 'admin'
            ? 'No tenant selected. Please select a tenant from the top bar.'
            : 'No tenant assigned. Please contact your administrator.'
          }
        </Alert>
      )}

      {/* Mobile Card View */}
      {isMobile ? (
        <Grid container spacing={2}>
          {filteredPatients.map((patient) => (
            <Grid item xs={12} key={patient.id}>
              <Card sx={{ position: 'relative' }}>
                <CardContent sx={{ pb: 1 }}>
                  {/* Three-dot menu */}
                  <IconButton
                    onClick={(e) => handleMenuClick(e, patient)}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  {/* Header with name and status */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
                    <Box display="flex" alignItems="center" flex={1}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
                      <Link
                        component="button"
                        variant="h6"
                        onClick={() => navigate(`/patients/${patient.id}`)}
                        sx={{ textAlign: 'left', fontWeight: 'medium', fontSize: '1.1rem' }}
                      >
                        {patient.full_name}
                      </Link>
                    </Box>
                    <Chip
                      label={patient.status}
                      color={getStatusColor(patient.status) as any}
                      size="small"
                      onClick={(e) => handleStatusClick(e, patient)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>

                  {/* Patient Details */}
                  <Box sx={{ pl: 4 }}>
                    {patient.email && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        📧 {patient.email}
                      </Typography>
                    )}
                    {patient.phone && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        📱 {patient.phone}
                      </Typography>
                    )}
                    {patient.date_of_birth && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        🎂 {calculateAge(patient.date_of_birth)} years old
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      📅 Last meeting: {formatLastMeeting(patient.last_meeting_date)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {filteredPatients.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  No patients found
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        /* Desktop Table View */
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Meeting</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPatients.map((patient) => (
                <TableRow key={patient.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(`/patients/${patient.id}`)}
                        sx={{ textAlign: 'left' }}
                      >
                        {patient.full_name}
                      </Link>
                    </Box>
                  </TableCell>
                  <TableCell>{patient.email || '-'}</TableCell>
                  <TableCell>{patient.phone || '-'}</TableCell>
                  <TableCell>{patient.date_of_birth ? `${calculateAge(patient.date_of_birth)} years` : '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={patient.status}
                      color={getStatusColor(patient.status) as any}
                      size="small"
                      onClick={(e) => handleStatusClick(e, patient)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{formatLastMeeting(patient.last_meeting_date)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuClick(e, patient)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <PersonIcon sx={{ mr: 2 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleScheduleMeeting}>
          <ScheduleIcon sx={{ mr: 2 }} />
          Schedule Meeting
        </MenuItem>
      </Menu>

      {/* Status Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        {selectedPatient?.status === 'active' ? (
          <MenuItem onClick={handleFinish}>
            Finish Treatment
          </MenuItem>
        ) : (
          <MenuItem onClick={handleRevive}>
            Revive Patient
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Patient Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogTitle>
            {editingPatient ? 'Edit Patient' : 'Add New Patient'}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register('first_name', { required: 'First name is required' })}
              fullWidth
              label="First Name"
              margin="normal"
              error={!!errors.first_name}
              helperText={errors.first_name?.message as string}
            />
            <TextField
              {...register('last_name', { required: 'Last name is required' })}
              fullWidth
              label="Last Name"
              margin="normal"
              error={!!errors.last_name}
              helperText={errors.last_name?.message as string}
            />
            <TextField
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              fullWidth
              label="Email (Optional)"
              type="email"
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message as string}
            />
            <TextField
              {...register('phone')}
              fullWidth
              label="Phone"
              margin="normal"
            />
            <TextField
              {...register('date_of_birth', { required: false })}
              fullWidth
              label="Date of Birth (Optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ required: false }}
              margin="normal"
            />
            {editingPatient && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  {...register('status' as any)}
                  label="Status"
                  defaultValue={editingPatient.status}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="finished">Finished</MenuItem>
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingPatient ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default PatientsPage;